import { HTMLAttributes, useEffect, useRef, useState } from 'react'
import { useEvent } from 'react-use'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'

import Image from '@/components/base/Image'
import { useLenis } from '@/contexts/lenis'
import { useLoaderStore } from '@/store/loader'

import { cn } from '@/lib/utils'
import { WPImage, WPPageData } from '@/types'

export type RibbonPicturesProps = HTMLAttributes<HTMLElement> &
  Partial<{
    items: WPImage[]
    theme: WPPageData['acf']['theme'] | 'default'
  }>

const RibbonPictures = ({
  items,
  theme = 'default',
  className,
  ...props
}: RibbonPicturesProps) => {
  const wrapper = useRef<HTMLDivElement>(null!)
  const loaded = useLoaderStore((state) => state.pageLoaded)
  const { lenis } = useLenis()
  const [diff, setDiff] = useState(0)

  const calculateDiff = () => {
    if (!wrapper.current || !items || items.length === 0) return
    const imageWrapper = wrapper.current.querySelector('[data-motion-ribbon]')
    if (imageWrapper) {
      const computedDiff =
        (imageWrapper as HTMLElement).offsetWidth - window.innerWidth
      setDiff(computedDiff)
    }
  }

  useEvent('resize', calculateDiff)

  useEffect(() => {
    calculateDiff()
  }, [items])

  useGSAP(
    (context) => {
      if (!lenis || !loaded || items?.length === 0) return
      const imageWrapper =
        context && context.selector
          ? context.selector('[data-motion-ribbon]')[0]
          : null

      const timeline = gsap.timeline({
        scrollTrigger: {
          trigger: wrapper.current,
          scrub: 1,
          invalidateOnRefresh: true,
          start: () => 'top bottom',
          end: () => 'bottom top'
        }
      })

      if (diff < 0) {
        timeline.set(imageWrapper, { x: (diff * -1) / 2 })
      } else {
        timeline.fromTo(
          imageWrapper,
          {
            x: 0
          },
          {
            x: () => diff * -1,
            ease: 'linear'
          }
        )
      }
      return () => {
        timeline.kill()
      }
    },
    {
      dependencies: [lenis, loaded, diff],
      scope: wrapper,
      revertOnUpdate: true
    }
  )

  return items && items?.length > 0 ? (
    <section
      ref={wrapper}
      {...props}
      className={cn([
        {
          'bg-transparent': theme === 'default',
          'bg-dark-100': theme === 'light',
          'bg-dark-600': theme === 'dark'
        },
        className
      ])}
    >
      <div className="overflow-hidden">
        <div
          className="flex h-[28rem] w-max items-center justify-center gap-[1.6rem]"
          data-motion-ribbon
        >
          {items?.map((item) => (
            <div key={item.id} className="h-full flex-shrink-0">
              <Image source={item} size="large" className="h-full w-auto" />
            </div>
          ))}
        </div>
      </div>
    </section>
  ) : null
}

export default RibbonPictures
